/**
 * Cart Totals Component
 * Displays subtotal, discount, extra-charge list, and grand total.
 *
 * Extra charges (Transportation / Labour / Installation / custom) are
 * MANAGED via a dialog opened from the Cart action bar — NOT inline here.
 * This component only RENDERS the charges that already exist plus inline
 * amount-edit / remove controls.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import { X, Truck } from 'lucide-react';
import { formatINR } from '../../lib/store';
import { roundINR } from '../../lib/pdfFormat';

export function CartTotals({
  subtotal,
  overallDiscType,
  setOverallDiscType,
  overallDiscValue,
  setOverallDiscValue,
  extraCharges = [],
  setExtraCharges,
}) {
  const overallDiscount = overallDiscType === 'percent'
    ? subtotal * overallDiscValue / 100
    : overallDiscValue;
  const extrasTotal = extraCharges.reduce((s, ec) => s + (parseFloat(ec.amount) || 0), 0);
  const grandTotal = subtotal - overallDiscount + extrasTotal;

  const handleRemove = (idx) => {
    setExtraCharges(extraCharges.filter((_, i) => i !== idx));
  };

  const handleUpdateAmount = (idx, val) => {
    setExtraCharges(extraCharges.map((ec, i) => i === idx ? { ...ec, amount: parseFloat(val) || 0 } : ec));
  };

  return (
    <div className="p-4 bg-heda-cream/60 border-t border-heda-line">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-heda-sub">Subtotal</span>
        <span className="font-mono-data font-semibold">{formatINR(roundINR(subtotal))}</span>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-heda-sub">Additional Discount</span>
          <Select value={overallDiscType} onValueChange={setOverallDiscType}>
            <SelectTrigger className="rounded-xl bg-heda-cream/50 w-20 h-7 text-xs border-heda-line" data-testid="overall-disc-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">%</SelectItem>
              <SelectItem value="amount">₹</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            min="0"
            value={overallDiscValue}
            onChange={(e) => setOverallDiscValue(parseFloat(e.target.value) || 0)}
            className="rounded-xl w-20 h-7 text-xs text-center bg-white border-heda-line"
            data-testid="overall-disc-value"
          />
        </div>
        <span className={`font-mono-data text-sm ${overallDiscount > 0 ? 'text-red-700' : 'text-heda-mute'}`}>
          {overallDiscount > 0 ? `-${formatINR(roundINR(overallDiscount))}` : '—'}
        </span>
      </div>

      {/* Existing extra-charge rows (no inline composer — adding is done
          via the "Extra Charge" button in the action bar below). */}
      {extraCharges.length > 0 && (
        <div className="space-y-1.5 mb-2" data-testid="extra-charges-list">
          {extraCharges.map((ec, i) => (
            <div key={i} className="flex items-center justify-between gap-2 bg-sky-50 border border-sky-200 rounded-xl px-2 py-1" data-testid={`extra-charge-row-${i}`}>
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <Truck className="w-3.5 h-3.5 text-sky-700 flex-shrink-0" />
                <span className="text-sm font-medium text-sky-700 truncate">{ec.label}</span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-sky-700 text-xs">₹</span>
                <Input
                  type="number"
                  min="0"
                  value={ec.amount}
                  onChange={(e) => handleUpdateAmount(i, e.target.value)}
                  className="rounded-xl w-24 h-7 text-xs text-right bg-white border-sky-200 font-mono-data"
                  data-testid={`extra-charge-amount-${i}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(i)}
                  className="rounded-xl h-6 w-6 p-0 text-red-700 hover:bg-red-50"
                  data-testid={`remove-extra-charge-${i}`}
                  title="Remove this charge"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Separator className="my-2 bg-heda-line" />
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-heda-ink">Grand Total</span>
        <span className="text-xl font-bold font-mono-data text-heda-olive-dark" data-testid="grand-total">
          {formatINR(roundINR(grandTotal))}
        </span>
      </div>
      <p className="text-[10px] text-heda-mute text-right mt-1">GST @ 18% included in prices</p>
    </div>
  );
}

export default CartTotals;
